import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const AutomationRuleForm = () => {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showActionsHelp, setShowActionsHelp] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rule_type: 'trigger_based',
    trigger_event: '',
    conditions: '',
    actions: '',
    is_active: true
  });

  const isEditing = Boolean(ruleId);

  useEffect(() => {
    if (ruleId) {
      fetchRule();
    }
  }, [ruleId]);

  const fetchRule = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/automation/rules/${ruleId}`);
      const rule = response.data;
      setFormData({
        name: rule.name || '',
        description: rule.description || '',
        rule_type: rule.rule_type || 'trigger_based',
        trigger_event: rule.trigger_event || '',
        conditions: rule.conditions ? JSON.stringify(rule.conditions, null, 2) : '',
        actions: rule.actions ? JSON.stringify(rule.actions, null, 2) : '',
        is_active: rule.is_active !== undefined ? rule.is_active : true
      });
    } catch (error) {
      console.error('Error fetching rule:', error);
      setError(error.response?.data?.error || 'Failed to load rule');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        conditions: formData.conditions ? JSON.parse(formData.conditions) : null,
        actions: formData.actions ? JSON.parse(formData.actions) : null
      };

      if (isEditing) {
        await api.put(`/api/automation/rules/${ruleId}`, payload);
      } else {
        await api.post('/api/automation/rules', payload);
      }
      
      navigate('/automation-rules');
    } catch (error) {
      console.error('Error saving rule:', error);
      setError(error.response?.data?.error || error.response?.data?.message || `Failed to ${isEditing ? 'update' : 'create'} rule`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this automation rule? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      await api.delete(`/api/automation/rules/${ruleId}`);
      navigate('/automation-rules');
    } catch (error) {
      console.error('Error deleting rule:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to delete rule');
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/dashboard')}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <button
          onClick={() => navigate('/automation-rules')}
          className="breadcrumb-link"
        >
          Automation Rules
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {isEditing ? 'Edit Rule' : 'New Rule'}
        </span>
      </nav>

      {/* Rule Form Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              {isEditing ? '✏️ Edit Automation Rule' : '➕ Create New Automation Rule'}
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              {isEditing ? 'Update automation rule configuration' : 'Configure a new marketing automation trigger and action'}
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Basic Information */}
            <div>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: 'var(--gray-900)',
                marginBottom: '1rem'
              }}>
                Basic Information
              </h3>
              
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Rule Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-input"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Welcome Email on Signup"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description" className="form-label">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    className="form-input"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    placeholder="Describe what this automation rule does"
                  />
                </div>
              </div>
            </div>

            {/* Rule Configuration */}
            <div>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: 'var(--gray-900)',
                marginBottom: '1rem'
              }}>
                Rule Configuration
              </h3>
              
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div className="form-group">
                  <label htmlFor="rule_type" className="form-label">
                    Rule Type *
                  </label>
                  <select
                    id="rule_type"
                    name="rule_type"
                    className="form-input"
                    value={formData.rule_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="trigger_based">Trigger Based</option>
                    <option value="time_based">Time Based</option>
                    <option value="behavior_based">Behavior Based</option>
                    <option value="condition_based">Condition Based</option>
                  </select>
                  <small style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                    Choose when this rule should execute
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="trigger_event" className="form-label">
                    Trigger Event *
                  </label>
                  <select
                    id="trigger_event"
                    name="trigger_event"
                    className="form-input"
                    value={formData.trigger_event}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a trigger event...</option>
                    <option value="user_signup">User Signup</option>
                    <option value="user_login">User Login</option>
                    <option value="form_submission">Form Submission</option>
                    <option value="purchase_complete">Purchase Complete</option>
                    <option value="cart_abandoned">Cart Abandoned</option>
                    <option value="email_opened">Email Opened</option>
                    <option value="email_clicked">Email Clicked</option>
                    <option value="page_view">Page View</option>
                    <option value="lead_created">Lead Created</option>
                    <option value="lead_qualified">Lead Qualified</option>
                    <option value="campaign_started">Campaign Started</option>
                    <option value="campaign_completed">Campaign Completed</option>
                    <option value="subscription_created">Subscription Created</option>
                    <option value="subscription_cancelled">Subscription Cancelled</option>
                    <option value="contact_updated">Contact Updated</option>
                    <option value="tag_added">Tag Added</option>
                    <option value="segment_entered">Segment Entered</option>
                    <option value="webhook_received">Webhook Received</option>
                  </select>
                  <small style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                    The event that triggers this rule
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="conditions" className="form-label">
                    Conditions (JSON)
                  </label>
                  <textarea
                    id="conditions"
                    name="conditions"
                    className="form-input"
                    value={formData.conditions}
                    onChange={handleInputChange}
                    rows="5"
                    style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                    placeholder='{"field": "value", "operator": "equals"}'
                  />
                  <small style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                    Optional: Define conditions that must be met for the rule to execute
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="actions" className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    Actions (JSON)
                    <button
                      type="button"
                      onClick={() => setShowActionsHelp(!showActionsHelp)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'var(--primary-600)',
                        fontSize: '1rem'
                      }}
                      title="View documentation"
                    >
                      ❓
                    </button>
                  </label>
                  
                  {showActionsHelp && (
                    <div style={{
                      backgroundColor: 'var(--primary-50)',
                      border: '1px solid var(--primary-200)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem',
                      fontSize: '0.875rem'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                        <h4 style={{ margin: 0, color: 'var(--primary-900)', fontSize: '0.95rem', fontWeight: '600' }}>
                          📚 Actions Documentation
                        </h4>
                        <button
                          type="button"
                          onClick={() => setShowActionsHelp(false)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            color: 'var(--gray-600)',
                            padding: 0
                          }}
                        >
                          ✕
                        </button>
                      </div>
                      
                      <div style={{ color: 'var(--gray-700)', lineHeight: '1.5' }}>
                        <p style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                          Define actions as a JSON object with action types as keys. Multiple actions can be executed for each rule.
                        </p>
                        
                        <div style={{ marginBottom: '1rem' }}>
                          <strong style={{ color: 'var(--primary-900)' }}>1. Webhook (n8n, Zapier, Make)</strong>
                          <pre style={{
                            backgroundColor: 'var(--gray-800)',
                            color: 'var(--gray-100)',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            overflow: 'auto',
                            fontSize: '0.8rem',
                            marginTop: '0.5rem'
                          }}>{`{
  "webhook": {
    "url": "https://your-n8n.com/webhook/abc123",
    "method": "POST",
    "headers": {"Content-Type": "application/json"},
    "custom_data": {"campaign": "welcome"}
  }
}`}</pre>
                        </div>
                        
                        <div style={{ marginBottom: '1rem' }}>
                          <strong style={{ color: 'var(--primary-900)' }}>2. HTTP Request (with placeholders)</strong>
                          <pre style={{
                            backgroundColor: 'var(--gray-800)',
                            color: 'var(--gray-100)',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            overflow: 'auto',
                            fontSize: '0.8rem',
                            marginTop: '0.5rem'
                          }}>{`{
  "http_request": {
    "url": "https://api.example.com/contacts",
    "method": "POST",
    "headers": {"Authorization": "Bearer token"},
    "body": {
      "email": "{{email}}",
      "name": "{{name}}",
      "score": "{{lead_score}}"
    }
  }
}`}</pre>
                        </div>
                        
                        <div style={{ marginBottom: '1rem' }}>
                          <strong style={{ color: 'var(--primary-900)' }}>3. Send Email</strong>
                          <pre style={{
                            backgroundColor: 'var(--gray-800)',
                            color: 'var(--gray-100)',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            overflow: 'auto',
                            fontSize: '0.8rem',
                            marginTop: '0.5rem'
                          }}>{`{
  "send_email": {
    "template_id": 5
  }
}`}</pre>
                        </div>
                        
                        <div style={{ marginBottom: '1rem' }}>
                          <strong style={{ color: 'var(--primary-900)' }}>4. Update Lead Score</strong>
                          <pre style={{
                            backgroundColor: 'var(--gray-800)',
                            color: 'var(--gray-100)',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            overflow: 'auto',
                            fontSize: '0.8rem',
                            marginTop: '0.5rem'
                          }}>{`{
  "update_lead_score": {
    "score_change": 10
  }
}`}</pre>
                        </div>
                        
                        <div style={{ marginBottom: '1rem' }}>
                          <strong style={{ color: 'var(--primary-900)' }}>5. Add to Email List</strong>
                          <pre style={{
                            backgroundColor: 'var(--gray-800)',
                            color: 'var(--gray-100)',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            overflow: 'auto',
                            fontSize: '0.8rem',
                            marginTop: '0.5rem'
                          }}>{`{
  "add_to_list": {
    "list_id": 3
  }
}`}</pre>
                        </div>
                        
                        <div style={{ marginBottom: '1rem' }}>
                          <strong style={{ color: 'var(--primary-900)' }}>6. Multiple Actions</strong>
                          <pre style={{
                            backgroundColor: 'var(--gray-800)',
                            color: 'var(--gray-100)',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            overflow: 'auto',
                            fontSize: '0.8rem',
                            marginTop: '0.5rem'
                          }}>{`{
  "webhook": {
    "url": "https://n8n.com/webhook/abc"
  },
  "send_email": {
    "template_id": 5
  },
  "update_lead_score": {
    "score_change": 15
  }
}`}</pre>
                        </div>
                        
                        <p style={{ marginBottom: 0, fontSize: '0.85rem', color: 'var(--primary-800)' }}>
                          <strong>💡 Tip:</strong> Use <code>{'{{'}</code>field_name<code>{'}}'}</code> placeholders in HTTP requests to insert dynamic data from the triggered entity.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <textarea
                    id="actions"
                    name="actions"
                    className="form-input"
                    value={formData.actions}
                    onChange={handleInputChange}
                    rows="8"
                    style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                    placeholder={`{
  "webhook": {
    "url": "https://your-n8n-instance.com/webhook/your-hook-id",
    "method": "POST",
    "headers": {"Content-Type": "application/json"},
    "custom_data": {"campaign": "welcome"}
  }
}`}
                  />
                  <small style={{ display: 'block', color: 'var(--gray-600)', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    <strong>Available action types:</strong> webhook, http_request, send_email, update_lead_score, add_to_list, trigger_ai_agent
                  </small>
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '600',
                color: 'var(--gray-900)',
                marginBottom: '1rem'
              }}>
                Status
              </h3>
              
              <div className="form-group form-check">
                <input
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  className="form-checkbox"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <label htmlFor="is_active" className="form-check-label">
                  Active
                </label>
                <small style={{ display: 'block', color: 'var(--gray-600)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  Only active rules will be executed
                </small>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid var(--gray-200)'
          }}>
            <div>
              {isEditing && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="btn btn-danger"
                  disabled={loading}
                >
                  Delete Rule
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => navigate('/automation-rules')}
                className="btn btn-outline"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Rule' : 'Create Rule')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutomationRuleForm;
